import {useScoutingReports} from "@/utils/scoutingReportData";
import ScoutingReportsStatus from "@/components/ScoutingReportsStatus";
import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";

import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import ScoreRing from "@/components/ScoreRing";
import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import {VERDICTS, getVerdict, reportPlayerName} from "@/utils/scoutingReports";

import "./index.scss";

const VERDICT_OPTIONS = VERDICTS.map((v) => ({value: v.id, label: v.label}));

const searchKey = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase();

// Relazioni condivise del club nella stagione selezionata.
export default function RelazioniPage() {
	const navigate = useNavigate();
	const data = useScoutingReports();
	const {reports, canWrite} = data;
	const [verdict, setVerdict] = useState(null);
	const [query, setQuery] = useState("");

	const shown = useMemo(() => {
		const key = searchKey(query).trim();
		return reports.filter(
			(r) =>
				(!verdict || r.verdict === verdict.value) &&
				(!key || searchKey(reportPlayerName(r.player)).includes(key)),
		);
	}, [reports, verdict, query]);

	const newReport = {
		icon: <AddIcon />,
		label: "Nuova relazione",
		title: "Nuova relazione",
		disabled: !canWrite,
		onClick: () => navigate("/scouting/relazioni/new"),
	};
	// Nell'intestazione solo icone; il testo resta nel pulsante della lista vuota
	const actions = [
		{...newReport, label: undefined},
		{
			icon: <TuneIcon />,
			title: "Template",
			onClick: () => navigate("/scouting/relazioni/templates"),
		},
	];

	return (
		<Page className="page-container relazioni-page">
			<Breadcrumbs
				current="Relazioni"
				actions={<MainActions actions={actions} />}
			/>
			<ScoutingReportsStatus data={data} />
			{data.status === "ready" && (
				<div className="relazioni-stack">
					{reports.length > 0 && (
						<div className="relazione-meta">
							<div>
								<VLFTextField
									label="Giocatore"
									placeholder="Cerca per nome"
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									size="small"
								/>
							</div>
							<div>
								<VLFSelectNew
									label="Verdetto"
									placeholder="Tutti"
									options={VERDICT_OPTIONS}
									value={verdict}
									onChange={setVerdict}
									sorted={false}
									noMargin
								/>
							</div>
						</div>
					)}

					{shown.length ? (
						<div className="relazioni-grid">
							{shown.map((r) => {
								const v = getVerdict(r.verdict);
								return (
									<button
										key={r.id}
										type="button"
										className="relazione-card"
										style={{
											"--verdict-color": v?.color || "var(--color-text-muted)",
											"--verdict-bg": v?.bg || "var(--color-surface-soft)",
										}}
										onClick={() => navigate("/scouting/relazioni/" + r.id)}
									>
										<span className="relazione-card__head">
											<span className="relazione-card__verdict">
												{v?.label}
											</span>
											<ScoreRing value={r.average} max={5} size="small" />
										</span>
										<strong className="relazione-card__player">
											{reportPlayerName(r.player)}
										</strong>
										<span className="relazione-card__meta">
											{[
												r.player?.goalkeeper && "Portiere",
												r.player?.dateOfBirth &&
													moment(r.player.dateOfBirth).year(),
											]
												.filter(Boolean)
												.join(" · ")}
										</span>
										{r.judgment && (
											<span className="relazione-card__judgment">
												{r.judgment}
											</span>
										)}
										<span className="relazione-card__foot">
											{[
												r.reportDate && moment(r.reportDate).format("DD/MM/YY"),
												r.author?.name,
												r.template?.name,
											]
												.filter(Boolean)
												.join(" · ")}
										</span>
									</button>
								);
							})}
						</div>
					) : (
						<div className="relazioni-empty">
							<strong>
								{reports.length
									? "Nessuna relazione con questi filtri"
									: "Ancora nessuna relazione"}
							</strong>
							{!reports.length && (
								<>
									<span>
										Scrivi la prima dalla scheda di un giocatore o da qui.
									</span>
									<MainActions actions={[newReport]} />
								</>
							)}
						</div>
					)}
				</div>
			)}
		</Page>
	);
}
